import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNotify, useQueryWithStore, useRedirect } from 'react-admin';
import { useProfile } from '../../core/hooks/UseProfile';
import {
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@material-ui/core';
import { CreationContactContext } from './creationsteps/CreationContactContext';
import { isEmpty } from 'lodash';
import FormTitleBar from '../../core/components/FormTitleBar';
import useNotifyServerError from '../../core/hooks/useNotifyServerError';
import UserApi from '../../core/apis/UserApi';
import { useStyles } from './creationsteps/StyleStep';
import UserDetailsStep from './creationsteps/UserDetailsStep';
import AssignPermissionGroupsStep from './creationsteps/AssignPermissionGroupsStep';
import SelectPermissionGroupsStep from './creationsteps/SelectPermissionGroupsStep';
import EmailNotificationsStep from './creationsteps/EmailNotificationsStep';
import WizardStepper from '../../core/components/wizardStepper/WizardStepper';
import { GenerateMessageOnLeave } from '../../core/components/wizardStepper/GenerateMessageOnLeave';

export const STEP = {
  contactDetails: 'contactDetails',
  selectPermissionGroups: 'selectPermissionGroups',
  assignPermissionGroups: 'assignPermissionGroups',
  accessRoles: 'accessRoles',
  emailNotifications: 'emailNotifications'
};
const UserCreate = props => {
  const { permissions = [] } = props;
  const classes = useStyles();
  const creation = true;
  const { profile, loading } = useProfile();
  const notifyServerError = useNotifyServerError();
  const notify = useNotify();
  const [activeStep, setActiveStep] = useState(0);
  const [contactContext, setContactContext] = useState({});
  const [contactCreated, setContactCreated] = useState({});
  const userSet = useRef(null);
  const [isNextAvailable, setIsNextAvailable] = useState(true);
  const [isBackDisabled, setIsBackDisabled] = useState(false);
  const [isSaveAndCloseStep, setIsSaveAndCloseStep] = useState(false);

  const [confirmWarningIsOpen, setConfirmWarningIsOpen] = useState(false);
  const [selectedContactForImport, setSelectedContactForImport] = useState(
    null
  );
  const [activeListOfSteps, setActiveListOfSteps] = useState([]);
  const [isDefaultListOfStepsSet, setIsDefaultListOfStepsSet] = useState(false);
  const redirect = useRedirect();
  const [assignedPermissionGroups, setAssignedPermissionGroups] = useState([]);
  const [selectedAssignmentMode, setSelectedAssignmentMode] = useState(
    'manual'
  );
  const [selectedPermissionGroups, setSelectedPermissionGroups] = useState({
    funds: [],
    investors: []
  });
  const [accessRoles, setAccessRoles] = useState([]);
  const [selectedAccessRoles, setSelectedAccessRoles] = useState({ roles: [] });
  const authorizationsGridToDeleteRef = useRef([]);
  const [permissionGroupsUploaded, setPermissionGroupsUploaded] = useState([]);
  const [isNewImportToDo, setIsNewImportToDo] = useState(false);
  const shouldQuitRef = useRef(false);
  const [submitButtonClicked, setSubmitButtonClicked] = useState(null);
  const [isSpinnerOnNextButton, setIsSpinnerOnNextButton] = useState(false);
  const [isSpinnerOnSaveButton, setIsSpinnerOnSaveButton] = useState(false);
  const [isFirstStepPristine, setIsFirstStepPristine] = useState(true);
  const providerFormContext = useMemo(
    () => ({ contactContext, setContactContext }),
    [contactContext, setContactContext]
  );
  const [permissionsStaging, setPermissionsStaging] = useState([]);
  const [leavingPromptListOfSteps, setLeavingPromptListOfSteps] = useState([]);

  const [
    hasEditUserPermission,
    hasWriteUserAuthorizationPermission,
    hasReadUserAuthorizationNaPermission,
    hasReadUserRolesPermission,
    hasWriteUserRolesPermission,
    hasComposeEmailPermission
  ] = useMemo(
    () => [
      permissions.includes('AUTH_EDIT_USER'),
      permissions.includes('AUTH_WRITE_USER_AUTHORIZATION'),
      permissions.includes('AUTH_READ_USER_AUTHORIZATION_NA'),
      permissions.includes('AUTH_READ_USER_ROLE'),
      permissions.includes('AUTH_WRITE_USER_ROLE'),
      permissions.includes('AUTH_COMPOSE_EMAIL')
    ],
    [permissions]
  );

  const isLastStep = useCallback(() => {
    return !!(
      !isEmpty(activeListOfSteps) && activeListOfSteps.length === activeStep + 1
    );
  }, [activeStep, activeListOfSteps]);

  const isItSaveAndCloseStep = useCallback(() => {
    if (!isEmpty(activeListOfSteps)) {
      return !!(
        (activeListOfSteps.length === 2 &&
          activeStep === 0 &&
          activeListOfSteps.find(
            step => step.name === STEP.emailNotifications
          )) ||
        (activeListOfSteps[activeStep].name === STEP.assignPermissionGroups &&
          !isLastStep()) ||
        (activeListOfSteps[activeStep].name === STEP.accessRoles &&
          !isLastStep())
      );
    }
  }, [activeListOfSteps, activeStep, isLastStep]);

  useEffect(() => {
    if (activeStep === 0 && contactContext.detailsFormState) {
      const {
        valid: isDetailsFormValid,
        submitting: isDetailsFormSubmitting,
        pristine: isDetailsFormPristine
      } = contactContext.detailsFormState;

      setIsFirstStepPristine(isDetailsFormPristine);
      setIsNextAvailable(isDetailsFormValid && !isDetailsFormSubmitting);
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.selectPermissionGroups
    ) {
      // Disable/Enable <next> button on the "Permission Groups - Select" step
      isEmpty(selectedPermissionGroups.funds) &&
      (isEmpty(selectedPermissionGroups.investors) ||
        selectedPermissionGroups.investors.length > 50)
        ? setIsNextAvailable(false)
        : setIsNextAvailable(true);

      if (selectedAssignmentMode === 'import') {
        isEmpty(selectedContactForImport)
          ? setIsNextAvailable(false)
          : setIsNextAvailable(true);
      }
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.assignPermissionGroups
    ) {
      permissionsStaging.length > 0
        ? setIsNextAvailable(true)
        : setIsNextAvailable(false);
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.accessRoles
    ) {
      isEmpty(selectedAccessRoles.roles)
        ? setIsNextAvailable(false)
        : setIsNextAvailable(true);
    }

    if (
      !isEmpty(activeListOfSteps) &&
      activeListOfSteps[activeStep].name === STEP.emailNotifications
    ) {
      setIsNextAvailable(true);
    }

    if (activeStep === 1 && !hasEditUserPermission) {
      setIsBackDisabled(true);
    } else {
      setIsBackDisabled(false);
    }

    isItSaveAndCloseStep()
      ? setIsSaveAndCloseStep(true)
      : setIsSaveAndCloseStep(false);
  }, [
    isItSaveAndCloseStep,
    hasEditUserPermission,
    selectedPermissionGroups,
    selectedAccessRoles,
    assignedPermissionGroups,
    activeStep,
    accessRoles,
    activeListOfSteps,
    selectedContactForImport,
    selectedAssignmentMode,
    contactContext,
    permissionsStaging.length
  ]);

  // Preload fund list. It will be needed by some steps
  useQueryWithStore({
    type: 'GET_LIST',
    resource: 'funds',
    payload: {}
  });

  const handleClose = useCallback(() => {
    userSet.current !== null
      ? redirect(`/users/${userSet.current.userId}`)
      : redirect('list', '/users');
  }, [redirect]);

  const handleConfirmWarningClose = () => setConfirmWarningIsOpen(false);

  const setDefaultListOfSteps = useCallback(() => {
    let stepsByDefault = [];
    let stepsForLeavingPrompt = [];
    stepsByDefault.push({
      name: STEP.contactDetails,
      label: 'Contact Details',
      description:
        'Please fill in the fields to create Contact. All other details, such as addresses, phone numbers, etc, you can edit later.',
      component: UserDetailsStep,
      stepNo: 1
    });
    stepsForLeavingPrompt.push({
      name: STEP.contactDetails,
      leavingPromptLabel: 'Contact Details',
      completed: false
    });

    if (
      hasWriteUserAuthorizationPermission &&
      hasReadUserAuthorizationNaPermission
    ) {
      stepsByDefault.push(
        {
          name: STEP.selectPermissionGroups,
          label: 'Permission Groups - Select',
          description: null,
          component: SelectPermissionGroupsStep,
          stepNo: 2
        },
        {
          name: STEP.assignPermissionGroups,
          label: 'Permission Groups - Assign',
          description: isNewImportToDo
            ? 'Below are assigned permissions of the selected contact. Select which ones you want to import.'
            : 'Select permission groups in the matrix below. ',
          component: AssignPermissionGroupsStep,
          stepNo: 3
        }
      );
      stepsForLeavingPrompt.push({
        name: STEP.assignPermissionGroups,
        leavingPromptLabel: 'Access Rights',
        completed: false
      });
    }

    if (hasComposeEmailPermission) {
      stepsByDefault.push({
        name: STEP.emailNotifications,
        label: 'Email Notifications',
        description: null,
        component: EmailNotificationsStep,
        stepNo: 5
      });
      stepsForLeavingPrompt.push({
        name: STEP.emailNotifications,
        leavingPromptLabel: 'Email Notifications',
        completed: false
      });
    }
    return [stepsByDefault, stepsForLeavingPrompt];
  }, [
    hasWriteUserAuthorizationPermission,
    hasReadUserAuthorizationNaPermission,
    hasComposeEmailPermission,
    isNewImportToDo
  ]);

  useEffect(() => {
    const [stepsByDefaults, stepsForLeavingPrompt] = setDefaultListOfSteps();
    setLeavingPromptListOfSteps(stepsForLeavingPrompt);
    if (!isDefaultListOfStepsSet) {
      setActiveListOfSteps(stepsByDefaults);
      setIsDefaultListOfStepsSet(true);
    }
  }, [
    setActiveListOfSteps,
    isDefaultListOfStepsSet,
    setIsDefaultListOfStepsSet,
    setDefaultListOfSteps
  ]);

  const getActiveStep = () => {
    const step = activeListOfSteps[activeStep];
    return (
      <step.component
        {...props}
        variant="standard"
        creation={creation}
        onSubmitEnd={onSubmitEnd}
        contactCreated={contactCreated}
        permissions={permissions}
        connectedUserProfile={profile}
        accessRoles={accessRoles}
        setAccessRoles={setAccessRoles}
        setSelectedAccessRoles={setSelectedAccessRoles}
        setContactCreated={setContactCreated}
        userSet={userSet}
        isNewImportToDo={isNewImportToDo}
        setIsNewImportToDo={setIsNewImportToDo}
        setIsNextAvailable={setIsNextAvailable}
        setActiveListOfSteps={setActiveListOfSteps}
        setLeavingPromptListOfSteps={setLeavingPromptListOfSteps}
        leavingPromptListOfSteps={leavingPromptListOfSteps}
        setDefaultListOfSteps={setDefaultListOfSteps}
        activeListOfSteps={activeListOfSteps}
        selectedAssignmentMode={selectedAssignmentMode}
        permissionGroupsUploaded={permissionGroupsUploaded}
        selectedPermissionGroups={selectedPermissionGroups}
        confirmWarningIsOpen={confirmWarningIsOpen}
        clearStates={clearStates}
        handleConfirmWarningClose={handleConfirmWarningClose}
        selectedContactForImport={selectedContactForImport}
        setAssignedPermissionGroups={setAssignedPermissionGroups}
        setSelectedPermissionGroups={setSelectedPermissionGroups}
        setSelectedContactForImport={setSelectedContactForImport}
        authorizationsGridToDeleteRef={authorizationsGridToDeleteRef}
        hasReadUserRolesPermission={hasReadUserRolesPermission}
        hasWriteUserRolesPermission={hasWriteUserRolesPermission}
        hasWriteUserAuthorizationPermission={
          hasWriteUserAuthorizationPermission
        }
        hasReadUserAuthorizationNaPermission={
          hasReadUserAuthorizationNaPermission
        }
        hasComposeEmailPermission={hasComposeEmailPermission}
        shouldQuitRef={shouldQuitRef}
        handleClose={handleClose}
        submitButtonClicked={submitButtonClicked}
        setIsSpinnerOnNextButton={setIsSpinnerOnNextButton}
        setIsSpinnerOnSaveButton={setIsSpinnerOnSaveButton}
        setSelectedAssignmentMode={setSelectedAssignmentMode}
        setPermissionsStaging={setPermissionsStaging}
        permissionsStaging={permissionsStaging}
      />
    );
  };

  const onSubmitEnd = () => {
    activeListOfSteps.length === activeStep + 1
      ? handleClose()
      : setActiveStep(activeStep + 1);
    setIsSpinnerOnNextButton(false);
    setIsSpinnerOnSaveButton(false);
    setConfirmWarningIsOpen(false);
  };

  const handleBackForm = () => {
    if (activeListOfSteps[activeStep].name === STEP.assignPermissionGroups) {
      authorizationsGridToDeleteRef.current = [];
      setPermissionsStaging([]);
      setSelectedAssignmentMode('manual');
    }

    setContactContext(currentContext => ({
      ...currentContext,
      createContactForm: undefined,
      currentForm: undefined
    }));
    setIsNewImportToDo(false);
    setActiveStep(activeStep - 1);
  };

  const handleNextForm = async e => {
    const buttonClicked = e.currentTarget.getAttribute('buttonkey');
    setSubmitButtonClicked(buttonClicked);

    if (activeListOfSteps[activeStep].name !== STEP.contactDetails) {
      buttonClicked === 'saveAndClose'
        ? setIsSpinnerOnSaveButton(true)
        : setIsSpinnerOnNextButton(true);
    }

    if (activeListOfSteps[activeStep].name === STEP.emailNotifications) {
      onSubmitEnd();
      notify('ra.notification.created_contact_successful');
      return;
    }

    if (activeListOfSteps[activeStep].name === STEP.assignPermissionGroups) {
      const permissionToUpload = permissionsStaging.map(item => ({
        userId: contactCreated.id,
        fundId: item.fundId,
        investorId: item.investorId,
        groupId: item.groupId
      }));
      const permissionAlreadyUploaded = permissionGroupsUploaded.map(item => ({
        userId: contactCreated.id,
        fundId: item.fundId,
        investorId: item.investorId,
        groupId: item.groupId
      }));

      function areObjectsEqual(obj1, obj2) {
        return (
          obj1.userId === obj2.userId &&
          obj1.fundId === obj2.fundId &&
          obj1.investorId === obj2.investorId &&
          obj1.groupId === obj2.groupId
        );
      }

      function hasObject(arr, obj) {
        return arr.some(item => areObjectsEqual(item, obj));
      }

      function getNewPermissionsToUpload(
        permissionsAlreadyUploaded,
        permissionsToUpload
      ) {
        return permissionsToUpload.filter(
          obj => !hasObject(permissionsAlreadyUploaded, obj)
        );
      }

      const newPermissionToUpload = getNewPermissionsToUpload(
        permissionAlreadyUploaded,
        permissionToUpload
      );

      try {
        if (authorizationsGridToDeleteRef.current.length > 0) {
          await UserApi.deleteUserauthorizations(
            authorizationsGridToDeleteRef.current
          );
          authorizationsGridToDeleteRef.current = [];
        }

        if (newPermissionToUpload.length > 0) {
          const response = await UserApi.createUserauthorizations(
            newPermissionToUpload
          );
          setPermissionGroupsUploaded(
            response.map(authorization => ({
              userId: contactCreated.id,
              fundId: authorization.fundId,
              investorId: authorization.investorId,
              groupId: authorization.groupId,
              id: `${contactCreated.userId}:${authorization.investorId}:${authorization.fundId}:${authorization.groupId}`
            }))
          );
        }
        const updatedListOfStep = leavingPromptListOfSteps.map(step => {
          if (step.name === STEP.assignPermissionGroups) {
            return { ...step, completed: true };
          }
          return step;
        });
        setLeavingPromptListOfSteps(updatedListOfStep);
        if (buttonClicked === 'saveAndClose') {
          handleClose();
          notify('ra.notification.created');
        } else {
          setIsNewImportToDo(false);
          onSubmitEnd();
        }
      } catch (error) {
        notifyServerError(error, undefined);
      }
    }

    if (activeListOfSteps[activeStep].name === STEP.accessRoles) {
      if (buttonClicked === 'saveAndClose') {
        handleClose();
        notify('ra.notification.created_contact_successful');
      } else {
        onSubmitEnd();
      }
    }

    if (activeListOfSteps[activeStep].name === STEP.contactDetails) {
      if (buttonClicked === 'saveAndClose') {
        shouldQuitRef.current = true;
      }
      document
        .getElementById('contactDetailsForm')
        .dispatchEvent(new Event('submit', { cancelable: true }));
    }

    if (activeListOfSteps[activeStep].name === STEP.selectPermissionGroups) {
      if (selectedAssignmentMode === 'import') {
        onSubmitEnd();
      }
    }

    contactContext.currentForm && contactContext.currentForm.submit();
  };

  // Display the information of the user created in the top left of the wizard once we reach the step 2
  const displayContactCreatedInfo = () => {
    return (
      <FormTitleBar
        recordLabel={'User ID: ' + contactCreated.userId}
        name={contactCreated.firstName + ' ' + contactCreated.lastName}
        status={contactCreated.status}
        statusLabel={'Status:'}
        showAvatar={true}
        avatarContent={
          (contactCreated.firstName
            ? contactCreated.firstName.substring(0, 1).toUpperCase()
            : '') +
          (contactCreated.lastName
            ? contactCreated.lastName.substring(0, 1).toUpperCase()
            : '')
        }
      />
    );
  };

  const getDescriptionContainerStyle = useCallback(
    (currentStep, currentListOfStep) => {
      if (!isEmpty(currentListOfStep)) {
        switch (currentListOfStep[currentStep].name) {
          case STEP.contactDetails:
            return classes.descriptionContainer_contactDetails;
          case STEP.accessRoles:
            return classes.descriptionContainer_accessRoles;
          default:
            return classes.descriptionContainer_default;
        }
      }
    },
    [
      classes.descriptionContainer_contactDetails,
      classes.descriptionContainer_accessRoles,
      classes.descriptionContainer_default
    ]
  );

  const clearStates = () => {
    setSelectedPermissionGroups({
      funds: [],
      investors: []
    });
    setSelectedContactForImport(null);
    setAssignedPermissionGroups([]);
    setContactContext({ permissionGroupSelect: undefined });
  };

  const handleCheckBoxChange = event => {
    if (event.target.value === 'import' && !isEmpty(permissionGroupsUploaded)) {
      return null;
    } else {
      clearStates();
      setIsNewImportToDo(false);
      setSelectedAssignmentMode(event.target.value);
    }
  };

  const handleImportRadioButtonClick = () => {
    if (!isEmpty(permissionGroupsUploaded)) {
      setConfirmWarningIsOpen(true);
    }
  };

  const displayImportPermissionRadioButton = () =>
    activeListOfSteps[activeStep].name === STEP.selectPermissionGroups ? (
      <>
        <RadioGroup
          row
          defaultValue={selectedAssignmentMode}
          value={selectedAssignmentMode}
          onChange={event => handleCheckBoxChange(event)}
        >
          <FormControlLabel
            value="manual"
            control={<Radio />}
            label="Manually assign permission groups"
          />
          <FormControlLabel
            value="import"
            onMouseDown={handleImportRadioButtonClick}
            onKeyDown={e => console.log(e)}
            control={<Radio />}
            label="Import permission groups from existing contact"
          />
        </RadioGroup>
        {/* In the permission assignment step, we display the correct description according to the selection mode selected in the previous step */}
        {activeListOfSteps[activeStep].description &&
          activeListOfSteps[activeStep].name === STEP.assignPermissionGroups &&
          selectedAssignmentMode === 'manual' && (
            <span className={classes.description}>
              {activeListOfSteps[activeStep].description[0]}
            </span>
          )}
        {activeListOfSteps[activeStep].description &&
          activeListOfSteps[activeStep].name === STEP.assignPermissionGroups &&
          selectedAssignmentMode === 'import' && (
            <span className={classes.description}>
              {activeListOfSteps[activeStep].description[1]}
            </span>
          )}
      </>
    ) : (
      <span className={classes.description}>
        {activeListOfSteps[activeStep].description}
      </span>
    );

  if (isEmpty(activeListOfSteps) || loading) {
    return (
      <div className={classes.progress}>
        <CircularProgress />
      </div>
    );
  } else {
    return (
      <WizardStepper
        hasRadioButtons={true}
        displayRadiosButtons={displayImportPermissionRadioButton}
        handleClose={handleClose}
        getActiveStep={getActiveStep}
        handleBackForm={handleBackForm}
        handleNextForm={handleNextForm}
        resource={'Contact'}
        resourceCreated={contactCreated}
        displayResourceCreatedInfo={displayContactCreatedInfo}
        shouldDisplayResourceCreatedInfo={true}
        getDescriptionContainerStyle={getDescriptionContainerStyle}
        isBackDisabled={isBackDisabled}
        isNextAvailable={isNextAvailable}
        activeListOfSteps={activeListOfSteps}
        activeStep={activeStep}
        isSpinnerOnNextButton={isSpinnerOnNextButton}
        isSpinnerOnSaveButton={isSpinnerOnSaveButton}
        isSaveAndCloseStep={isSaveAndCloseStep}
        providerFormContext={providerFormContext}
        customContext={CreationContactContext}
        generateMessageOnLeave={
          <GenerateMessageOnLeave
            listOfSteps={leavingPromptListOfSteps}
            resource={'Contact'}
            resourceName={`${contactCreated.firstName} ${contactCreated.lastName}`}
          />
        }
        isFirstStepPristine={isFirstStepPristine}
      />
    );
  }
};

export default UserCreate;
